import { useEffect, useState, type FC } from 'react';
import useSWRMutation from 'swr/mutation';
import {
  DTOupdateOrderProductsInformation,
  IMinDetails,
  IProductDelivery,
  IproductReferenceDataInCountry
} from '../../../../types/orders';
import {
  NudosButton,
  NudosGenericModal,
  NudosModalContent,
  NudosSelectButton,
  NudosStaticAlertInComponent,
  NudosTextInput
} from '../../../../components/NudosComponents';
import NewSerial from './components/NewSerial';
import { SWR_KEYS } from '../../../../constants/constants';
import { generateSerialAutomatic, updateOrderProductsInformation } from '../../../../services/orders';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../utils/displayNudosStandardNotifications';
import { EXCLUDE_PILL } from '../../../../types/pills';
import { detectDuplicateElements } from '../../../../utils/detectDuplicateElements';
import { IProductInfo } from '../../../../types/products';
import BordModalConfirm from '../../../../components/NudosComponents/BordModalConfirm/BordModalConfirm';
import { updateStockOrderShipmentSerials } from '../../../../services/stockOrders';
import { IStockOrderShipmentSerials } from '../../../../types/stockOrders';
import './SerialsModal.scss';

interface ISerialsModal {
  changeShowModal?: () => void;
  updateWithoutLoaders?: () => void;
  singleProduct?: IProductDelivery;
  products?: IProductDelivery[];
  productInfo?: IProductInfo;
  isStockOrder?: boolean;
  customDisabledButton?: boolean;
  references?: IproductReferenceDataInCountry[];
}

const SerialsModal: FC<ISerialsModal> = ({
  singleProduct,
  products = [],
  references,
  changeShowModal,
  updateWithoutLoaders,
  productInfo = {
    name: 'Mac book pro 14',
    units: 10
  },
  isStockOrder = false
}) => {
  const [errorList, setErrorList] = useState<IMinDetails[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isAutomaticSerial, setIsAutomaticSerial] = useState<boolean[]>([]);
  const [isAutomaticSerialAllUnits, setIsAutomaticSerialAllUnits] = useState<boolean>(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false);
  const [isGeneratedSerial, setIsGeneratedSerial] = useState<boolean[]>([]);
  const [forAllUnits, setForAllUnits] = useState<boolean>(false);
  const [automaticSerialProductIds, setAutomaticSerialProductIds] = useState<number[]>([]);
  const [isUpdateSerials, setIsUpdateSerials] = useState<boolean>(false);
  const [productsWithStock, setProductsWithStock] = useState<number[]>([]);
  const [stockSerials, setStockSerials] = useState<Record<number, string>>({});

  const { trigger: updateSerials, isMutating: isLoadingUpdateSerials } = useSWRMutation(
    SWR_KEYS.update_serials,
    async (
      __,
      {
        arg
      }: {
        arg: IStockOrderShipmentSerials | DTOupdateOrderProductsInformation[];
      }
    ) => {
      const response = (await isStockOrder)
        ? updateStockOrderShipmentSerials(arg as IStockOrderShipmentSerials)
        : updateOrderProductsInformation(arg as DTOupdateOrderProductsInformation[]);
      return response;
    },
    {
      onSuccess: async response => {
        if (Array.isArray(response)) showErrors(response);
        else {
          await updateWithoutLoaders?.();
          await closeModal();
          await displaySuccessNotification({ customJSXMessage: <>Seriales actualizados correctamente</> });
          setLoading(false);
        }
      }
    }
  );

  const showErrors = (errors: IMinDetails[]) => {
    setLoading(false);
    const existErrors = errors?.filter(element => element.error);
    setErrorList(existErrors);
  };

  const { serials = [] } = singleProduct ?? {};
  const defaultSerials = isStockOrder
    ? products?.map(product => product.serial || '')
    : products?.map(product => product.serial || '').filter(Boolean) ?? [];
  const [serialByProducts, setSerialByProducts] = useState<string[]>(defaultSerials);

  const [newSerialText, setNewSerialText] = useState('');

  const profileAndLinePills = Object.values(EXCLUDE_PILL) as EXCLUDE_PILL[];
  const excludeWidthAndLength: EXCLUDE_PILL[] = profileAndLinePills.filter(
    props => props !== EXCLUDE_PILL.Ancho && props !== EXCLUDE_PILL.Largo
  );
  const pillsEntries = Object.entries(productInfo?.pills || {})
    .filter(([key, value]) => value && key && !excludeWidthAndLength.includes(key as EXCLUDE_PILL))
    .slice(0, 3)
    .map(([key, value]) => value)
    .join(' - ');

  const hasDuplicatesElements = detectDuplicateElements(serialByProducts);

  const isUsingAutomaticSerials = (): boolean => {
    return isAutomaticSerialAllUnits || automaticSerialProductIds.length > 0;
  };

  const filterProductsWithValidSerials = (serialList: string[]): string[] => {
    return serialList.filter(serial => {
      return serial !== null && serial !== undefined && serial.trim() !== '';
    });
  };

  const productsWithValidSerials = filterProductsWithValidSerials(serialByProducts);

  const hasStockSerials = productsWithStock.length > 0;
  const existSerials =
    hasStockSerials || (isStockOrder ? defaultSerials?.some((serial) => !!serial) : defaultSerials?.length > 0);

  const renderStylesNewSerial = existSerials ? 'existNewSerial' : 'notExistNewSerial';

  const closeModal = () => changeShowModal && changeShowModal();

  const submit = async () => {
    const formdata = serialByProducts
      .map((singleSerial, index) => {
        const productId = products[index].id;
        const hasStockSerial = productsWithStock.includes(productId);

        if (hasStockSerial) {
          return null;
        }
        if (stockSerials[productId] === singleSerial) {
          return null;
        }

        return isStockOrder
          ? {
              productId: productId,
              serial: singleSerial,
              organizationId: null
            }
          : {
              saleId: products[index].saleId,
              serial: singleSerial
            };
      })
      .filter(item => item !== null);

    if (formdata.length === 0) {
      displayErrorNotification({
        customJSXMessage: <>Todos los productos tienen stock asignado. No hay seriales para actualizar.</>
      });
      return;
    }

    try {
      setLoading(true);
      await updateSerials(formdata as IStockOrderShipmentSerials | DTOupdateOrderProductsInformation[]);
    } catch (error) {
      setLoading(false);
      displayErrorNotification({
        customJSXMessage: <>Ocurrió un error al actualizar los seriales. Por favor, intenta nuevamente.</>
      });
    }
  };

  const handleSubmit = async () => {
    if ((isAutomaticSerialAllUnits && automaticSerialProductIds?.length <= 0) || forAllUnits) {
      await callGenerateSerialAutomatic(products.map(product => product?.id));
      return;
    }

    if (automaticSerialProductIds?.length > 0) {
      await Promise.all([
        serialByProducts?.length > 0 ? submit() : Promise.resolve(),
        callGenerateSerialAutomatic(automaticSerialProductIds)
      ]);
      return;
    }

    await submit();
  };

  const removeError = (serial: string) => {
    const filterSerial = errorList?.filter(error => error?.serial !== serial) || [];
    setErrorList([...filterSerial]);
  };

  const handleAutomaticSerial = (productId: number, index: number) => {
    const newAutomaticSerials = [...isAutomaticSerial];
    const isCurrentlySelected = newAutomaticSerials[index];
    newAutomaticSerials[index] = !isCurrentlySelected;

    setIsAutomaticSerial(newAutomaticSerials);

    if (!isCurrentlySelected) {
      setAutomaticSerialProductIds(prev => [...prev, productId]);
    } else {
      setAutomaticSerialProductIds(prev => prev.filter(id => id !== productId));
    }
  };

  const handleAutomaticSerialAllUnits = () => {
    setIsAutomaticSerialAllUnits(!isAutomaticSerialAllUnits);
  };

  const callGenerateSerialAutomatic = async (productsIds: number[]) => {
    setLoading(true);
    try {
      const filterProducts = products.filter(({ id }) => productsIds.includes(id));
      if (filterProducts?.length === 0) {
        displayErrorNotification({
          customJSXMessage: <>No se encontró el producto para generar el serial automático</>
        });
        return;
      }
      const response = await generateSerialAutomatic(productsIds, true);
      if (response.data === 'success') {
        const newGeneratedSerials = [...isGeneratedSerial];
        productsIds.forEach(productId => {
          const productIndex = products.findIndex(product => product?.id === productId);
          if (productIndex !== -1) {
            newGeneratedSerials[productIndex] = true;
          }
        });
        setIsGeneratedSerial(newGeneratedSerials);
        displaySuccessNotification({
          customJSXMessage: <>Todos los seriales se generaron con exito</>
        });
        setIsOpenConfirmModal(false);
        setIsUpdateSerials(true);
        if (isAutomaticSerialAllUnits || automaticSerialProductIds?.length > 0) {
          updateWithoutLoaders?.();
          closeModal();
        }
      }
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al generar serial automatico</>
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAutomaticSerial = () => {
    setIsOpenConfirmModal(false);
  };

  const handleCancelConfirmAutomaticSerial = () => {
    setIsOpenConfirmModal(false);
    setIsAutomaticSerial([]);
    setAutomaticSerialProductIds([]);
  };

  const handleCancelConfirmAutomaticSerialAllUnits = () => {
    const productIds = products.map(product => product?.id);
    setAutomaticSerialProductIds(productIds);
    setIsOpenConfirmModal(false);
    setForAllUnits(true);
  };

  const handleConfirmAutomaticOnlyUnitWithoutSerial = () => {
    const productsWithoutSerial = serialByProducts
      .map((serial, index) => ({ serial, index }))
      .filter(({ serial }) => serial === null || serial === undefined || serial.trim() === '');
    const productIdsWithoutSerial = productsWithoutSerial.map(({ index }) => products[index].id);
    setAutomaticSerialProductIds(productIdsWithoutSerial);
    setIsOpenConfirmModal(false);
    setForAllUnits(false);
  };

  const closeModalConfirm = () => {
    setIsOpenConfirmModal(false);
  };

  const getModalTitle = (): string => {
    if (hasStockSerials || existSerials) {
      return 'Editar serial';
    }

    return 'Agregar serial';
  };

  const shouldShowGeneratedPlaceholder = (index: number): boolean => {
    const hasSerial = serialByProducts[index] && serialByProducts[index].trim() !== '';
    const isAutomaticSelected = isAutomaticSerial[index];
    return !hasSerial && (isAutomaticSerialAllUnits || isAutomaticSelected);
  };

  const getSerialPlaceholder = (index: number): string => {
    const hasStockSerial = productsWithStock.includes(products[index]?.id);
    if (hasStockSerial) {
      return 'Serial de stock';
    }

    const isGeneratedSerialPlaceholder = shouldShowGeneratedPlaceholder(index) || forAllUnits;
    if (isGeneratedSerialPlaceholder) {
      return 'Serial generado';
    }

    return 'Escribe el serial';
  };

  const handleAssignSerials = () => {
    if (references && products) {
      const productsWithStockReservation: number[] = [];
      const newStockSerials: Record<number, string> = {};

      products.forEach(product => {
        const matchingReference = references?.find(ref => ref?.sku === product?.sku);

        if (!matchingReference?.items) return;

        const itemWithStock = matchingReference?.items?.find(
          item => item?.saleId === product?.saleId && item?.stockReservation?.stockProduct?.serial
        );

        if (!itemWithStock?.stockReservation?.stockProduct?.serial) return;

        productsWithStockReservation.push(product?.id);
        newStockSerials[product?.id] = itemWithStock?.stockReservation?.stockProduct?.serial ?? '';
      });

      setProductsWithStock(productsWithStockReservation);
      setStockSerials(newStockSerials);

      const initialSerials = products?.map(product => {
        if (productsWithStockReservation.includes(product?.id)) {
          return '';
        }
        return product?.serial || '';
      });
      setSerialByProducts(initialSerials);
    }
  };

  useEffect(() => {
    handleAssignSerials();
  }, [references, products]);

  useEffect(() => {
    if (isAutomaticSerial.some(isAutomatic => isAutomatic)) {
      setIsOpenConfirmModal(true);
    }
  }, [isAutomaticSerial]);

  useEffect(() => {
    if (isAutomaticSerialAllUnits && productsWithValidSerials?.length > 0) {
      setIsOpenConfirmModal(true);
    }
    if (!isAutomaticSerialAllUnits) {
      setForAllUnits(false);
    }
  }, [isAutomaticSerialAllUnits, productsWithValidSerials?.length]);

  if (isOpenConfirmModal) {
    return (
      <BordModalConfirm
        onCancel={!existSerials ? handleCancelConfirmAutomaticSerialAllUnits : handleCancelConfirmAutomaticSerial}
        onAccept={!existSerials ? handleConfirmAutomaticOnlyUnitWithoutSerial : handleConfirmAutomaticSerial}
        clickOutsideCallback={closeModalConfirm}
        closeModalCallback={closeModalConfirm}
        isLoading={loading}
        isDisabled={false}
        title={
          !existSerials
            ? '¿Para qué unidades deseas generar seriales?'
            : '¿Quieres generar un serial automático en todas las unidades?'
        }
        subtitle={
          !existSerials
            ? undefined
            : 'Al generar un serial automático se remplazará la información que escribiste. ¿Estás seguro que quieres continuar?'
        }
        textButtonCancel={!existSerials ? 'Para todas las unidades' : 'Mantener serial actual'}
        textButtonAccept={!existSerials ? 'Solo unidades sin serial' : 'Generar serial'}
      />
    );
  }

  return (
    <>
      {!!singleProduct?.id && (
        <NudosGenericModal
          closeModalCallback={closeModal}
          clickOutsideCallback={closeModal}
          modalContent={
            <NudosModalContent
              customWidth="580px"
              customHeight="446px"
              modalContentTitle={getModalTitle()}
              startHeader
              customHeaderClass="pl-32"
              toCloseModal={closeModal}
              CustomModalComponent={
                <div className="serialsModal">
                  <div className="subTitle">El serial es el identificador único de los productos.</div>
                  <NudosStaticAlertInComponent
                    customClassname="customNudosStaticAlertInComponent"
                    type="warning"
                    alertOptions={{
                      title: hasStockSerials ? (
                        <div>
                          Los productos con stock asignado mostrarán su serial correspondiente y podrán ser editados
                          solo en la orden de stock.{' '}
                          <strong>Aquí podrás editar los seriales de productos sin stock asignado.</strong>
                        </div>
                      ) : existSerials ? (
                        <div>
                          Al actualizar los seriales, sólo estás editando el identificador,{' '}
                          <strong>no estás cambiando el producto o modelo</strong>, y se actualizará en todos los
                          servicios asociados.
                        </div>
                      ) : (
                        'El serial sólo está asociado al producto o modelo'
                      )
                    }}
                    customTitleClass="customTitleAlert"
                    hideTitleClass
                  />
                  <div
                    className={`containerInfo ${
                      existSerials && products.length > 3 ? 'existSerialsButton' : 'emptySerialsButton'
                    }`}
                  >
                    <div className="productInfoContainer">
                      <span className="productItem">{productInfo.name}</span>
                      <span className="productItem">{`- ${pillsEntries}`}</span>
                      <span className="productLastItem">{`${productInfo.units} unidades`}</span>
                    </div>
                    <div
                      className={`${existSerials && products?.length > 3 ? 'existSerials' : ''} listSerialsContainer`}
                    >
                      {existSerials && (
                        <div className="columnSerials">
                          {products?.map((product, index) => {
                            const hasStockSerial = productsWithStock.includes(product.id);
                            const displaySerial = hasStockSerial ? stockSerials[product.id] : product.serial;

                            return (
                              <div key={index} className="mt-12 serialInputWrapper">
                                <NudosTextInput
                                  isFilled
                                  defaultValue={displaySerial ?? ''}
                                  label="Serial actual"
                                  placeholder={displaySerial ?? 'Sin serial'}
                                  customClassName="customSerialInput"
                                  isDeactivated
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className={renderStylesNewSerial}>
                        {serialByProducts?.map((serial, index) => {
                          return (
                            <NewSerial
                              key={index}
                              serials={serials}
                              existSerials={forAllUnits ? false : existSerials}
                              setNewSerialText={setNewSerialText}
                              index={index}
                              serialByProducts={serialByProducts}
                              defaultValue={
                                productsWithStock.includes(products[index]?.id)
                                  ? stockSerials[products[index]?.id]
                                  : undefined
                              }
                              setSerialByProducts={setSerialByProducts}
                              errorList={errorList}
                              removeError={removeError}
                              placeHolder={getSerialPlaceholder(index)}
                              disabled={
                                isGeneratedSerial[index] ||
                                shouldShowGeneratedPlaceholder(index) ||
                                forAllUnits ||
                                productsWithStock.includes(products[index]?.id)
                              }
                            />
                          );
                        })}
                      </div>
                      {existSerials && (
                        <div className="columnSerials">
                          {products?.map((product, index) => (
                            <div key={index} className="mt-12 serialInputWrapper">
                              <div className="containerCheckBoxAutomaticSerials">
                                <NudosSelectButton
                                  handleClickButton={() => handleAutomaticSerial(product?.id, index)}
                                  isButtonDisabled={!!product?.serial || productsWithStock.includes(product?.id)}
                                  isButtonActive={automaticSerialProductIds.includes(product?.id)}
                                />
                                <span>Utilizar serial automático</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {!existSerials && (
                    <div className="mt-12">
                      <div className="containerCheckBoxAutomaticSerials normal">
                        <NudosSelectButton
                          handleClickButton={handleAutomaticSerialAllUnits}
                          isButtonActive={isAutomaticSerialAllUnits}
                        />
                        <span>Utilizar serial automáticos en todas las unidades</span>
                      </div>
                    </div>
                  )}
                  <div
                    className={`buttonModal ${
                      existSerials && products.length > 3 ? 'existSerialsButton' : 'emptySerialsButton'
                    }`}
                  >
                    <NudosButton
                      buttonText="Guardar"
                      customWidth="380px"
                      customHeight="38px"
                      isButtonDisabled={
                        (!newSerialText && !isUsingAutomaticSerials()) ||
                        hasDuplicatesElements ||
                        !!errorList?.length ||
                        isUpdateSerials ||
                        productsWithStock.length === products.length
                      }
                      handleClick={handleSubmit}
                      isButtonLoading={isLoadingUpdateSerials || loading}
                    />
                  </div>
                </div>
              }
            />
          }
        />
      )}
    </>
  );
};

export default SerialsModal;
